import { useMutation, useQueryClient } from '@tanstack/react-query';

import { fbmOrdersV3 } from 'api/FBM/fbmOrdersV3';
import { ApiError } from 'shared/errors/ApiError';

import { fbmOrderDeepDiveQueryKeys } from './useGetOrderDeepDiveQuery';

/**
 * Cancels a partner order; on success invalidates the deep dive query for that order.
 *
 * @param {Omit<
 *   import('@tanstack/react-query').UseMutationOptions<
 *     Object,
 *     ApiError,
 *     { orderNum: string, fbmAccountName: string, reason: string }
 *   >,
 *   'mutationFn'
 * >} [mutationOptions={}]
 * @returns {import('@tanstack/react-query').UseMutationResult<
 *   Object,
 *   ApiError,
 *   { orderNum: string, fbmAccountName: string, reason: string }
 * >}
 */
export function useCancelPartnerOrderMutation(mutationOptions = {}) {
    const queryClient = useQueryClient();
    const { onSuccess, ...rest } = mutationOptions;

    return useMutation({
        mutationFn: async ({ fbmAccountName, orderNum, reason }) => {
            const response = await fbmOrdersV3.cancelPartnerOrder(orderNum, fbmAccountName, reason);
            if (!response?.status) {
                throw new ApiError(
                    response?.msg ?? 'Failed to cancel partner order',
                    response?.statusCode,
                    response?.data,
                );
            }
            return response.data;
        },
        onSuccess: (data, variables, ...args) => {
            queryClient.invalidateQueries({
                queryKey: fbmOrderDeepDiveQueryKeys.byOrderNumAndFbmAccountName(
                    variables.orderNum,
                    variables.fbmAccountName,
                ),
            });
            return onSuccess?.(data, variables, ...args);
        },
        ...rest,
    });
}
